import { Resources } from 'constants/resources';

import { useCallback, useEffect, useState } from 'react';

import { useGetList } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Provider } from 'types/embedded/provider';

type UseGatewayProviderAutofillResult = {
  fillProviderByGeneratedName: (name: string) => void;
  providerFilter: { name: string } | undefined;
  resetProviderFilter: () => void;
};

const getFirstWord = (value: string): string => value.trim().split(' ')[0].toLowerCase();

export function useGatewayProviderAutofill(): UseGatewayProviderAutofillResult {
  const { setValue } = useFormContext();
  const [searchWord, setSearchWord] = useState<string | null>(null);
  const [suggestedWord, setSuggestedWord] = useState<string | null>(null);

  const { data: providers, isFetching } = useGetList<Provider>(
    Resources.Providers,
    { filter: { name: searchWord }, pagination: { page: 1, perPage: 200 } },
    { enabled: !!searchWord },
  );

  useEffect(() => {
    if (!searchWord || !providers || isFetching) {
      return;
    }

    setSearchWord(null);

    const matchedProviders = providers.filter(
      (provider) => getFirstWord(provider.name) === searchWord,
    );

    if (matchedProviders.length === 1) {
      setValue('provider', matchedProviders[0]['@id'], {
        shouldDirty: true,
        shouldValidate: true,
      });
      setSuggestedWord(null);
      return;
    }

    setSuggestedWord(matchedProviders.length ? searchWord : null);
  }, [providers, isFetching, searchWord, setValue]);

  return {
    fillProviderByGeneratedName: useCallback(
      (name: string) => setSearchWord(getFirstWord(name) || null),
      [],
    ),
    providerFilter: suggestedWord ? { name: suggestedWord } : undefined,
    resetProviderFilter: useCallback(() => setSuggestedWord(null), []),
  };
}
